<template>
  <div class="agenda-form">
    <!-- agenda name -->
    <div class="agenda-form-row row">
      <div class="agenda-name">
        {{ $t("adminAgenda.sessionName") }}
        <a-input
          v-model="agendaName"
          :maxLength="agendaNameMaxLength"
          ref="toFocus"
          style="margin-top: 4px;"
          placeholder="Type session name here"
        />
        <span class="remainingText">{{ agendaNameRemainingLettersCount }}</span>
      </div>
      <!-- type -->
      <div class="agenda-type">
        {{ $t("adminAgenda.type") }}
        <a-select
          v-model="typeId"
          :class="{ selected: typeId }"
          style="width: 100%;"
        >
          <!-- <option value="" disabled selected>
            {{ $t("adminAgenda.selectType") }}
          </option> -->
          <a-select-option
            v-for="type in types"
            :key="type.agt_id"
            :value="type.agt_id"
          >
            {{ type.agt_name }}
          </a-select-option>
        </a-select>
      </div>
    </div>
    <!-- colors -->
    <div class="agenda-form-row" style="margin-top: -24px;">
      <color-picker
        :title="'Session card background color'"
        :label="'This is the background color of the single session card item.'"
        v-model="additionalDataValues.sessionCardBackgroundColor"
      />
    </div>
    <div class="agenda-form-row" style="margin-bottom: 20px;">
      <color-picker
        :title="'Session card text/tags/button color'"
        :label="
          'This is the text/tags/button color of the single session card item.'
        "
        v-model="additionalDataValues.sessionCardTextColor"
      />
    </div>
    <!-- moderators -->
    <div v-if="isSessionTypePanel" class="agenda-form-row">
      {{ "Moderators" }}
      <a-auto-complete
        :value="searchForModerator"
        :data-source="usernamesArr"
        placeholder="Search..."
        @select="onModeratorSelect"
        @search="onModeratorSearch"
      />
      <span class="remainingText"
        >Use autocomplete to select single or multiple moderators from the
        list.</span
      >
    </div>
    <!-- speaker_id -->
    <div class="agenda-form-row">
      {{ $t("adminAgenda.agendaSpeaker") }}
      <a-auto-complete
        :value="searchForUser"
        :data-source="usernamesArr"
        placeholder="Search..."
        @select="onUserSelect"
        @search="onUserSearch"
        style="margin-bottom: 0;"
      />
      <span class="remainingText"
        >Use autocomplete to select single or multiple speakers from the
        list.</span
      >
    </div>
    <div class="agenda-form-row row">
      <!-- import image -->
      <div class="agenda-name">
        {{ $t("adminAgenda.addImage") }}
        <a-select value="Image" style="width: 100%;">
          <a-select-option value="Image">
            Image
          </a-select-option>
        </a-select>
        <!-- <input
          type="file"
          ref="fileInput"
          @change="uploadImageAndAddToAdditional"
        /> -->
        <div class="upload-line">
          <a-upload
            action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
            list-type="picture-card"
            :file-list="fileList"
            @preview="handleImagePreview"
            @change="uploadImageAndAddToAdditional"
            style="width: 200px;"
          >
            <div v-if="fileList.length < 1">
              <a-icon type="plus" />
              <div class="ant-upload-text">
                Upload
              </div>
            </div>
          </a-upload>
          <a-modal
            :visible="previewVisible"
            :footer="null"
            @cancel="previewVisible = false"
          >
            <img alt="example" style="width: 100%" :src="previewImage" />
          </a-modal>
          <div class="upload-desc">
            <span
              >Please upload your session image here. Allowed are .jpeg or .png
              files.</span
            >
            <div class="filename" v-if="fileName">
              <img :src="$images.attachment" alt="attachment-svg" />
              {{ fileName }}
            </div>
          </div>
        </div>
      </div>
      <div class="agenda-name">
        <!-- location -->
        <div class="agenda-location">
          {{ $t("adminAgenda.location") }}
          <a-select v-model="locId" :class="{ selected: locId }">
            <a-select-option value="" disabled selected>
              Choose session location here
            </a-select-option>
            <a-select-option
              v-for="location in locations"
              :key="location.loc_id"
              :value="location.loc_id"
            >
              {{ location.name }}
            </a-select-option>
          </a-select>
        </div>
        <!-- host -->
        <div class="host">
          <a-switch v-model="isHostExternal" style="width: 44px;"></a-switch>
          <span>Host session externaly</span>
        </div>
      </div>
    </div>
    <!-- external host link -->
    <div v-if="isHostExternal" class="agenda-form-row">
      <span>External host link</span>
      <a-input
        :value="externalLinkSliced"
        @input="setExternalLink"
        :addon-before="URL_PREFIX"
      />
    </div>
    <div class="agenda-form-row row">
      <!-- tags -->
      <div class="agenda-form-row agenda-name">
        <span>{{ $t("adminAgenda.tagsTitle") }}</span>
        <a-select mode="multiple" v-model="agn_tags">
          <a-select-option value="" disabled selected>
            {{ $t("adminAgenda.selectLocation") }}
          </a-select-option>
          <a-select-option
            v-for="tag in tags"
            :key="tag.tag_id"
            :value="tag.tag_id"
          >
            {{ tag.tag_name }}
          </a-select-option>
        </a-select>
      </div>
      <!-- lang -->
      <div class="agenda-form-row agenda-name">
        {{ $t("adminAgenda.agendaLang") }}
        <a-input v-model="agendaLang" style="margin-top: 4px;" />
      </div>
    </div>
    <!-- dates -->
    <div class="agenda-form-row row">
      <div class="agenda-name">
        <span>{{ $t("adminAgenda.startDate") }}</span>
        <a-date-picker
          type="date"
          :value="inputDateFrom"
          @change="setDate('inputDateFrom', $event)"
        ></a-date-picker>
      </div>
      <div class="agenda-name">
        <span>{{ $t("adminAgenda.endDate") }}</span>
        <a-date-picker
          type="date"
          :value="inputDateTo"
          @change="setDate('inputDateTo', $event)"
        ></a-date-picker>
      </div>
    </div>
    <div class="agenda-form-row row">
      <div class="agenda-name">
        <span>{{ $t("adminAgenda.startTime") }}</span>
        <a-time-picker
          format="HH:mm"
          :placeholder="timePlaceholder"
          v-model="inputTimeFrom"
          style="width: 100%; border-radius: 4px; !important"
        ></a-time-picker>
      </div>
      <div class="agenda-name">
        <span>{{ $t("adminAgenda.endTime") }}</span>
        <a-time-picker
          format="HH:mm"
          :placeholder="timePlaceholder"
          v-model="inputTimeTo"
          style="width: 100%; border-radius: 4px;"
        ></a-time-picker>
      </div>
    </div>
    <!-- hosted_by -->
    <div class="agenda-form-row row">
      <div class="agenda-name">
        {{ $t("adminAgenda.agendaHosted") }}
        <a-auto-complete
          placeholder="Search for host..."
          :data-source="companiesStringArr"
          :value="searchForCmp"
          @select="onHostSelect"
          @search="onHostSearch"
          style="width: 100%;"
        />
      </div>
      <!-- breakout -->
      <div class="agenda-name">
        {{ $t("adminAgenda.agendaBreakout") }}
        <a-select v-model="breakout_id" :class="{ selected: breakout_id }">
          <a-select-option value="" selected>
            {{ $t("adminAgenda.selectBreakout") }}
          </a-select-option>
          <a-select-option
            v-for="ag in agendas"
            :key="ag.agn_id"
            :value="ag.agn_id"
          >
            {{ ag.agn_name }}
          </a-select-option>
        </a-select>
      </div>
    </div>
    <!-- lecturer -->
    <!-- <div class="agenda-form-row">
      {{ $t("adminAgenda.lecturers") }}
      <input type="text" v-model="lecturer" />
    </div> -->
    <!-- description -->
    <div class="agenda-form-row">
      <div class="agenda-form-item-w2">
        <div>{{ $t("adminAgenda.agendaDescNoHtml") }}</div>

        <!-- <textarea type="text" v-model="agendaDesc"></textarea> -->
        <a-textarea
          v-model="agendaDesc"
          placeholder="Description..."
          style="height: 120px; border-radius: 2px;"
        />
      </div>
    </div>
    <!-- additional -->
    <div class="agenda-form-row">
      <div class="agenda-form-item-w2">
        <div>{{ $t("adminAgenda.agendaAdditional") }}</div>

        <a-textarea
          style="height: 120px; border-radius: 2px;"
          v-model="agendaAdditional"
        ></a-textarea>
      </div>
    </div>
    <div class="buttons">
      <a-button @click="cancelEdit">
        {{ $t("button.cancel") }}
      </a-button>
      <a-button
        v-if="agenda.agn_id !== undefined"
        class="apply-button"
        @click="editAgenda"
      >
        {{ $t("button.editAgenda") }}
      </a-button>
      <a-button v-else class="apply-button" @click="createAgenda">
        {{ $t("button.createAgenda") }}
      </a-button>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import moment from "moment";
import AdminMixin from "@/views/admin-pages/adminMixin.js";
import ColorPicker from "./AgendaEditFormColorPicker.vue";
const IMAGE_KEY_NAME = "imageUrl";
export default {
  mixins: [AdminMixin],
  props: [
    "selectedTimezoneOffset",
    "locations",
    "agenda",
    "agendas",
    "types",
    "users",
    "tags",
    "companies",
  ],
  components: {
    ColorPicker,
  },
  data() {
    return {
      URL_PREFIX: "https://",
      moment,
      typeId: "2",
      inputDateFrom: "",
      inputTimeFrom: "",
      inputDateTo: "",
      inputTimeTo: "",
      locId: "",

      agendaName: "",
      agendaNameMaxLength: 200,
      agendaLang: "",

      agn_tags: [],

      additionalDataValues: {
        sessionCardBackgroundColor: "",
        sessionCardTextColor: "",
        moderator_id: "",
        external_link: "",
      },

      searchForModerator: "",
      speak_id: "",
      searchForUser: "",
      userInt: 0,

      breakout_id: "",

      hosted_by: "",
      searchForCmp: "",
      cmpInt: 0,

      isHostExternal: false,

      fileList: [],
      previewVisible: false,
      previewImage: "",

      agendaDesc: "",
      agendaAdditional: "",
      lecturer: "",
      showModal: false,
      timePlaceholder: this.$t("adminAgenda.hoursMinutes"),
    };
  },
  computed: {
    dateTimeFrom() {
      return (
        this.inputDateFrom + " " + moment(this.inputTimeFrom).format("HH:mm:ss")
      );
    },
    dateTimeTo() {
      return (
        this.inputDateTo + " " + moment(this.inputTimeTo).format("HH:mm:ss")
      );
    },
    agendaNameRemainingLettersCount() {
      const remainingLettersCount =
        this.agendaNameMaxLength - this.agendaName.length;
      return `${remainingLettersCount} characters remaining. Maximum ${this.agendaNameMaxLength} characters.`;
    },
    utcOffset() {
      return this.selectedTimezoneOffset * 60;
    },
    usernamesArr() {
      // this array needs to have an id in string because
      // ant-autocomplete component reports an error for 2 or more equal strings
      return this.users.map(
        (user) => `${user.usr_id}. ${user.usr_firstname} ${user.usr_lastname}`
      );
    },
    companiesStringArr() {
      return this.companies.map((cmp) => cmp.cmp_name);
    },
    fileName() {
      return this.fileList[0]?.name || "";
    },
    isSessionTypePanel() {
      const currentType = this.types.find((type) => type.agt_id == this.typeId);
      return currentType.agt_short === "panel";
    },
    externalLinkSliced() {
      return this.additionalDataValues.external_link.slice(
        this.URL_PREFIX.length
      );
    },
  },
  watch: {
    //When editing agenda, fill inputs with agenda data
    agenda: {
      deep: true,
      handler() {
        if (this.agenda.agn_id !== undefined) {
          this.fillInputs();
          this.fillDatesAndTimes();
        } else {
          this.emptyInputs();
        }
        this.focusInput();
      },
    },
    // every value that is in this obj will be writen to agendaAdditional value
    additionalDataValues: {
      deep: true,
      handler() {
        const parsedAgendaAdditional = this.agendaAdditional
          ? JSON.parse(this.agendaAdditional)
          : {};
        for (const key in this.additionalDataValues) {
          if (this.additionalDataValues[key])
            parsedAgendaAdditional[key] = this.additionalDataValues[key];
          else delete parsedAgendaAdditional[key];
        }
        this.agendaAdditional = JSON.stringify(parsedAgendaAdditional);
      },
    },
  },
  methods: {
    async handleImagePreview(file) {
      const getBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj);
      }
      this.previewImage = file.url || file.preview;
      this.previewVisible = true;
    },
    async uploadImageAndAddToAdditional(e) {
      this.fileList = e.fileList;

      const temp = this.agendaAdditional
        ? JSON.parse(this.agendaAdditional)
        : {};
      // delete previous images
      for (const key in temp) {
        if (key.includes(IMAGE_KEY_NAME)) {
          delete temp[key];
        }
      }

      // this if statement is needed due to ant upload (a-upload) firing this func on remove file as well
      if (e.file.status !== "removed") {
        let d = new FormData();
        d.append("image", this.fileList[0].originFileObj);
        let res;
        try {
          res = await api.uploadImage(d);
          let i = 0;
          // eslint-disable-next-line no-constant-condition
          while (1) {
            if (!temp[`${IMAGE_KEY_NAME}${i ? i : ""}`]) {
              temp[`${IMAGE_KEY_NAME}${i ? i : ""}`] = res.data.url;
              break;
            }
            i++;
          }
        } catch (e) {
          console.warn(e);
        }
      }

      this.agendaAdditional = JSON.stringify(temp);
    },
    refreshUsers() {
      if (this.userInt) {
        clearTimeout(this.userInt);
        this.userInt = null;
      }
      this.userInt = setTimeout(() => {
        this.$emit("get-users", this.searchForUser);
      }, 300);
    },
    refreshCmp() {
      if (this.cmpInt) {
        clearTimeout(this.cmpInt);
        this.cmpInt = null;
      }
      this.cmpInt = setTimeout(() => {
        this.$emit("get-companies", this.searchForCmp);
      }, 300);
    },
    dateTimeToUtc(datetime, offset) {
      return moment
        .utc(datetime)
        .utcOffset(-offset)
        .format("YYYY-MM-DD HH:mm:ss");
    },
    dateTimeToLocal(datetime, offset) {
      return moment(datetime, "YYYY-MM-DD HH:mm:ss")
        .utcOffset(offset)
        .format("YYYY-MM-DD HH:mm:ss");
    },
    async createAgenda() {
      this.callApiShowMessageGetData(
        api.createAgenda,
        this.$t("adminAgenda.agendaCreated")
      );
    },
    async editAgenda() {
      this.callApiShowMessageGetData(
        api.editAgenda,
        this.$t("adminAgenda.agendaUpdated")
      );
    },
    cancelEdit() {
      this.$emit("cancel-edit");
      // this.emptyInputs();
    },
    async callApiShowMessageGetData(api, successMessage) {
      if (this.checkErrorsAndReturnRequestParams() === false) {
        return;
      }
      let params = this.checkErrorsAndReturnRequestParams();
      //If Creating or Editing agenda
      if (this.agenda.agn_id) {
        params.agn_id = this.agenda.agn_id;
      }

      let apiObj = {
        api: api,
        params: params,
        onError: () => this.stopLoading(),
      };
      this.startLoading();
      await this.apiCall(apiObj);
      this.showSuccessMessage(successMessage);
      this.emptyInputs();
      this.$emit("get-agendas");
      this.stopLoading();
    },
    checkErrorsAndReturnRequestParams() {
      let params = {};
      //If start date doesn't exist
      if (this.inputDateFrom === "") {
        this.handleError(new Error("Please fill start time."));
        return false;
      }
      //If end date doesn't exist
      if (this.inputDateTo === "") {
        this.handleError(new Error("Please fill end time."));
        return false;
      }

      if (!this.typeId) {
        this.handleError(new Error("Please fill agenda type."));
        return false;
      } else {
        params.agt_id = this.typeId;
      }

      params.hosted_by = this.hosted_by || null;
      params.speaker_id = this.speak_id || null;
      params.language = this.agendaLang || null;
      params.agn_breakout = this.breakout_id || null;
      params.additional = this.agendaAdditional || null;
      params.agn_tags = this.agn_tags;

      //If both dates are valid
      if (this.inputDateFrom !== "" && this.inputDateTo !== "") {
        if (moment(this.dateTimeFrom) >= moment(this.dateTimeTo)) {
          this.handleError(new Error("Start date must be before end date."));
          return false;
        } else {
          params.agn_to = this.dateTimeToUtc(this.dateTimeTo, this.utcOffset);
          params.agn_from = this.dateTimeToUtc(
            this.dateTimeFrom,
            this.utcOffset
          );
        }
      }
      //If agenda name doesn't exist throw error, else append params
      if (this.agendaName.trim().length > 250) {
        this.handleError(new Error("Please use a shorter agenda name."));
        return false;
      }
      if (!this.agendaName.trim()) {
        this.handleError(new Error("Please fill agenda name."));
        return false;
      } else {
        params.agn_name = this.agendaName.trim();
      }

      if (this.agendaDesc.trim()) {
        params.agn_desc = this.agendaDesc.trim();
      }

      if (this.lecturer.trim().length > 250) {
        this.handleError(new Error("Please use a shorter lecturer name."));
        return false;
      }
      if (this.lecturer.trim()) {
        params.agn_lecturer = this.lecturer.trim();
      }

      if (this.locId) {
        params.loc_id = this.locId;
      }

      return params;
    },
    onModeratorSelect(indexedSearch) {
      const [usr_id, search] = indexedSearch.split(".");
      this.searchForModerator = search.trim();
      this.additionalDataValues.moderator_id = Number(usr_id);
    },
    onModeratorSearch(search) {
      this.searchForModerator = search.trim();
      this.refreshUsers();
    },
    onUserSelect(indexedSearch) {
      const [usr_id, search] = indexedSearch.split(".");
      this.searchForUser = search.trim();
      this.speak_id = Number(usr_id);
    },
    onUserSearch(search) {
      this.searchForUser = search.trim();
      this.refreshUsers();
    },
    onHostSelect(search) {
      this.searchForCmp = search.trim();
      const cmpObj = this.companies.find((cmp) => search === cmp.cmp_name);
      this.hosted_by = cmpObj.cmp_id;
    },
    onHostSearch(search) {
      this.searchForCmp = search.trim();
      this.refreshCmp();
    },
    focusInput() {
      this.$refs.toFocus.focus();
    },
    fillInputs() {
      this.fillDatesAndTimes();
      this.agendaName = this.agenda.agn_name;
      //Handling null with optional params
      this.locId = this.agenda.loc_id || "";
      this.agendaDesc = this.agenda.agn_desc || "";
      this.lecturer = this.agenda.agn_lecturer || "";
      this.typeId = this.agenda.agt_id || "";
      this.hosted_by = this.agenda.agn_hosted_by || "";
      this.searchForCmp = this.agenda.hosted_by_name || "";
      this.speak_id = this.agenda.speaker_id || "";
      this.searchForUser = `${this.agenda.usr_firstname} ${this.agenda.usr_lastname}`;
      this.breakout_id = this.agenda.agn_breakout || "";
      this.agendaAdditional = this.agenda.agn_additional || "";
      this.agendaLang = this.agenda.agn_language || "";
      this.parseAdditionalData();
      if (this.agenda.tags_ids && this.agenda.tags_ids.length > 1)
        this.agn_tags = this.agenda.tags_ids.split(",");
      else this.agn_tags = [];
    },
    parseAdditionalData() {
      const parsedAgendaAdditional = this.agendaAdditional
        ? JSON.parse(this.agendaAdditional)
        : {};
      if (parsedAgendaAdditional.external_link) this.isHostExternal = true;
      for (const key in this.additionalDataValues) {
        if (parsedAgendaAdditional[key])
          this.additionalDataValues[key] = parsedAgendaAdditional[key];
      }

      // handle image
      this.fileList = [
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: parsedAgendaAdditional[IMAGE_KEY_NAME],
        },
      ];
    },
    fillDatesAndTimes() {
      this.inputDateFrom = moment(
        this.agenda.agn_from,
        "DD.MM.YYYY HH:mm"
      ).format("YYYY-MM-DD");
      this.inputDateTo = moment(this.agenda.agn_to, "DD.MM.YYYY HH:mm").format(
        "YYYY-MM-DD"
      );
      this.inputTimeFrom = moment(this.agenda.agn_from, "DD.MM.YYYY HH:mm");
      this.inputTimeTo = moment(this.agenda.agn_to, "DD.MM.YYYY HH:mm");
    },
    emptyInputs() {
      this.agendaLang = "";
      this.agn_tags = [];
      this.speak_id = "";
      this.breakout_id = "";
      this.hosted_by = "";
      this.typeId = "2";
      // this.$refs["fileInput"].value = "";
      this.inputTimeFrom = "";
      this.inputDateFrom = "";
      this.inputDateTo = "";
      this.inputTimeTo = "";
      this.locId = "";
      this.agendaName = "";
      this.agendaDesc = "";
      this.lecturer = "";
      for (const key in this.additionalDataValues) {
        this.additionalDataValues[key] = "";
      }
      this.isHostExternal = false;
      this.fileList = [];
      this.searchForUser = "";
      this.searchForModerator = "";
      this.searchForCmp = "";
      this.agendaAdditional = "";
    },
    setDate(key, val) {
      this[key] = moment(val).format("YYYY-MM-DD");
    },
    setExternalLink(evt) {
      if (evt.target.value.length === 0)
        this.additionalDataValues.external_link = "";
      else
        this.additionalDataValues.external_link =
          this.URL_PREFIX + evt.target.value;
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
  color: #262626;
  font-size: 16px;
}
.agenda-form {
  width: 60%;
  // padding is because buttons are absolute
  // this absolute is dependant on position relative
  // in AgenaCreateEditSession component
  padding-bottom: 71px;
}
.agenda-form div {
  margin: 4px 0;
}
.agenda-form-row {
  display: flex;
  flex-direction: column;
  &.row {
    flex-direction: row;
    justify-content: space-between;
    gap: 16px;
  }
  .search {
    width: 50%;
    display: flex;
    align-items: center;
  }
}
.agenda-name,
.agenda-type {
  display: flex;
  flex-direction: column;
  width: 50%;
}
.agenda-location {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 0px !important;
}
.upload-line {
  display: flex;
  margin-top: 16px !important;
  .upload-desc {
    color: #001529;
    .filename {
      margin-top: 10px;
      font-size: 14px;
      color: #40a9ff;
    }
  }
}
.remainingText {
  font-size: 14px;
  margin: 8px 0 24px 0;
  color: #001529;
  width: 150%;
  // white-space: nowrap;
}
.host {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 14px !important;
  span {
    margin-left: 24px;
  }
}
input:focus,
input:active {
  outline: none;
}
input[type="text"] {
  border-radius: 4px;
}
.start,
.end {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.start-date,
.start-time,
.end-date,
.end-time {
  display: flex;
  flex-direction: column;
  // width: 50%;
}
.invalid-input-item {
  border: 1px solid red;
  box-shadow: 1px 1px 5px 1px red;
}
.agenda-form-dates {
  display: flex;
  flex-direction: column;
  width: 100%;
}
textarea {
  width: 100%;
  height: 70px;
  resize: none;
  border: 1px solid #dadada;
  background: #fff;
  border-radius: 10px;
  outline: none;
  padding: 10px;
}
.buttons {
  position: absolute;
  bottom: 24px;
  right: 24px;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
}
.apply-button {
  background-color: #2295d6;
  color: white;
}
.tag-input-wrap {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  input {
    width: 16px;
    height: 16px;
    margin: 0 20px 0 0;
  }
}
</style>
<style lang="scss">
.agenda-form {
  .ant-calendar-picker-input,
  .ant-time-picker-input {
    border-radius: 4px;
  }
}
</style>
